import {
  Box,
  Stack,
  styled,
  Tabs,
  Typography,
  type CSSObject,
} from '@mui/material';
import { theme } from '@rumblefish/ui';

export const StyledFirstTitle = styled(Typography)(({ theme }) => ({
  ...(theme.mixins.neutralGradient as CSSObject),
  letterSpacing: '-0.02em',
  display: 'flex',
  justifyContent: 'center',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  [theme.breakpoints.down('sm')]: {
    textAlign: 'center',
    alignSelf: 'center',
  },
}));

export const StyledCategoryItem = styled(Typography)<{ isSelected?: boolean }>(
  ({ theme, isSelected }) => ({
    color: theme.palette.invertedContent?.SECONDARY,
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'color .2s linear',
    lineHeight: '22px !important',
    textTransform: 'none',

    ...(isSelected && {
      color: theme.palette.invertedContent?.PRIMARY,
    }),

    [theme.breakpoints.down('md')]: {
      fontSize: '16px',
    },

    [theme.breakpoints.down('sm')]: {
      paddingLeft: 0,
      fontSize: '15px',
      maxWidth: 'min-content',
      textAlign: 'center',
    },
  })
);

export const StyledTabs = styled(Tabs)(({ theme }) => ({
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
  overflow: 'visible !important',
  '& button': {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      paddingLeft: theme.spacing(0),
      marginBottom: theme.spacing(1),
    },
  },
  '& .MuiTabs-indicator': {
    backgroundColor: 'transparent !important',
    borderTop: '1px solid white',
    overflow: 'visible !important',
    height: '1px',
    boxShadow: `0px 0px 4px 3px ${theme.palette.raspberryColors?.PLUS_1}, 0px 0px 1px 0px ${theme.palette.raspberryColors?.PLUS_1} !important`,
  },
  '& .MuiTabs-fixed': {
    overflow: 'visible',
  },
  '& .MuiTabs-scroller': {
    overflow: 'visible !important',
  },
}));

export const StyledWrapper = styled(Stack)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  gap: theme.spacing(6),
  marginBottom: theme.spacing(12),
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(3),
    marginBottom: theme.spacing(8),
  },
}));

export const StyledStack = styled(Stack)(({ theme }) => ({
  flexDirection: 'column',
  alignItems: 'center',
  marginBottom: theme.spacing(16),
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    justifyContent: 'flex-start',
  },
  [theme.breakpoints.down('sm')]: {},
}));

export const StyledSecondTitle = styled(Typography)(
  ({ theme: { breakpoints } }) => ({
    ...(theme.mixins.neutralGradient as CSSObject),
    color: 'white',
    textAlign: 'center',
    letterSpacing: '-0.02em',
    display: 'flex',
    [breakpoints.down('sm')]: {
      textAlign: 'left',
    },
  })
);

export const StyledServiceIconWrapper = styled('div')(({ theme }) => ({
  background:
    'radial-gradient(47.45% 48.31% at -0.77% 0%, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0) 100%), rgba(255, 255, 255, 0.03);',
  width: '70px',
  height: '70px',
  minWidth: '70px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '50%',
  [theme.breakpoints.down('md')]: {
    marginBottom: theme.spacing(1),
  },
  [theme.breakpoints.down('sm')]: {
    width: '55px',
    height: '55px',
    minWidth: '55px',
  },
}));

export const StyledServiceIconBackground = styled('div')(({ theme }) => ({
  background: 'rgba(246, 163, 81,0.05)',
  width: '55px',
  height: '55px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '50%',
  [theme.breakpoints.down('sm')]: {
    width: '40px',
    height: '40px',
    minWidth: '40px',
  },
}));

export const StyledServiceTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.invertedContent?.PRIMARY,
  transition: 'color .2s linear',
  '*': {
    display: 'inline',
  },
  '&:hover': {
    color: theme.palette.raspberryColors?.PLUS_1,
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: 17,
  },
}));

export const StyledUnderlinedText = styled('span')({
  textDecoration: 'none',
  backgroundImage: 'linear-gradient(currentColor, currentColor)',
  backgroundPosition: '0% 100%',
  backgroundRepeat: 'no-repeat',
  backgroundSize: '0% 1px',
  transition: 'background-size .2s',

  '&:hover': {
    backgroundSize: '100% 1px',
  },
});

export const StyledServiceDescription = styled(Typography)(({ theme }) => ({
  color: theme.palette.invertedContent?.SECONDARY,
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  [theme.breakpoints.down('sm')]: {
    fontSize: 14,
  },
}));

export const StyledServiceItemContainer = styled(Box)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.invertedBorder?.LIGHT}`,
  paddingBottom: theme.spacing(3),
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    paddingBottom: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}));

export const StyledServiceItemClickable = styled(Stack)(() => ({
  width: 'fit-content',
  cursor: 'pointer',
}));

export const StyledServiceContentStack = styled(Stack)(() => ({}));

export const StyledExpandButtonContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isOpen',
})<{ isOpen: boolean }>(({ theme, isOpen }) => ({
  backgroundColor: isOpen
    ? theme.palette.greyBackgroundColor?.SURFACE
    : theme.palette.greyAction?.ENABLED,
  borderRadius: '100%',
  width: '36px',
  minWidth: '36px',
  height: '36px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'background-color 0.2s ease-in-out',
  [theme.breakpoints.down('sm')]: {
    width: '30px',
    minWidth: '30px',
    height: '30px',
  },
}));

export const StyledSeeMoreButtonContainer = styled(Box)(() => ({
  width: '100%',
  display: 'flex',
  justifyContent: 'flex-end',
  marginTop: '8px',
}));
